import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { WingsIcon } from '../icons'
import './WingsButton.css'

/**
 * WingsButton Component
 * 
 * A versatile button component based on the Wingspans Style Guide.
 * 
 * @component
 * @example
 * // Basic usage
 * <WingsButton variant="primary" size="medium">
 *   Click me
 * </WingsButton>
 * 
 * @example
 * // With icon from registry
 * <WingsButton variant="primary" iconName="download">
 *   Download
 * </WingsButton>
 * 
 * @example
 * // Icon on right side
 * <WingsButton variant="secondary" iconName="arrow-right" iconPosition="right">
 *   Continue
 * </WingsButton>
 * 
 * @example
 * // Icon-only button
 * <WingsButton variant="primary" size="small" icon>
 *   <IconComponent />
 * </WingsButton>
 * 
 * @example
 * // Tertiary — standalone text CTA with size and padding
 * <WingsButton variant="tertiary" size="medium">View Details</WingsButton>
 *
 * @example
 * // Inline — strips padding/min-height so the button sits flush with surrounding content.
 * // Works with any variant, most useful with tertiary and off-site.
 * <WingsButton variant="tertiary" size="small" inline>Registered Nurse</WingsButton>
 */
const WingsButton = ({
  children,
  variant = 'primary',
  size = 'medium',
  icon = false,
  iconName,
  iconPosition = 'left',
  fullWidth = false,
  inline = false,
  loading,
  disabled = false,
  type = 'button',
  onClick,
  autoLoading = false,
  buttonRef = null,
  forceHoveredStyle = false,
  className = '',
  ...props
}) => {
  const [onClickRunning, setOnClickRunning] = useState(false)

  const autoLoadingClickHandler = async (e) => {
    setOnClickRunning(true)
    try {
      await onClick(e)
    } catch (err) {
      console.log("error in autoLoadingClickHandler", err)
    } finally {
      setOnClickRunning(false)
    }
  }

  loading ??= onClickRunning

  // Icon size mapping based on button size
  const iconSizeMap = {
    large: 'medium',   // 24px icon for 25px text
    medium: 'small',   // 20px icon for 20px text
    small: 'small',    // 20px icon for 18px text
    mini: 'mini',      // 16px icon for 16px text
    tiny: 'tiny',      // 14px icon for 14px text
  }

  // Build class names
  const classNames = [
    'w-button',
    `w-button-${size}`,
    `w-button-${variant}`,
    icon && 'w-button-icon',
    fullWidth && 'w-button-full',
    inline && 'w-button-inline',
    loading && 'w-button-loading',
    forceHoveredStyle && 'w-button-hovered',
    className
  ].filter(Boolean).join(' ')

  // Render icon if iconName is provided
  const renderIcon = () => {
    if (!iconName) return null
    return <WingsIcon icon={iconName} size={iconSizeMap[size]} />
  }

  // Build button content with icon positioning
  const buttonContent = () => {
    if (!iconName) {
      // No icon - just render children
      return children
    }

    // Button with icon and text
    const iconElement = renderIcon()

    if (icon) {
      // Icon-only button
      return (
        <span className={`w-button-icon-wrap w-button-icon-wrap-${size}`}>
          {iconElement}
        </span>
      )
    }

    if (iconPosition === 'right') {
      return (
        <span className={`w-button-icon-wrap w-button-icon-wrap-${size}`}>
          {children}
          {iconElement}
        </span>
      )
    }

    // Default: left position
    return (
      <span className={`w-button-icon-wrap w-button-icon-wrap-${size}`}>
        {iconElement}
        {children}
      </span>
    )
  }

  return (
    <button
      type={type}
      className={classNames}
      onClick={autoLoading ? autoLoadingClickHandler : onClick}
      disabled={disabled || loading}
      ref={buttonRef}
      {...props}
    >
      <span className={loading ? 'w-button-content-hidden' : ''}>
        {buttonContent()}
      </span>
      {loading && (
        <span className="w-button-spinner">
          <WingsIcon icon="spinner" size={iconSizeMap[size]} spin />
        </span>
      )}
    </button>
  )
}

WingsButton.propTypes = {
  /** Button content */
  children: PropTypes.node.isRequired,
  
  /** Button variant from style guide */
  variant: PropTypes.oneOf([
    'primary',
    'secondary',
    'tertiary',
    'off-site',
    'disabled',
    'tal-e',
    'on-blue',
    'on-dark',
    'public-page',
  ]),
  
  /** Button size from style guide */
  size: PropTypes.oneOf([
    'large',   // 25px
    'medium',  // 20px - desktop default
    'small',   // 18px - mobile default
    'mini',    // 16px
    'tiny',    // 14px
  ]),
  
  /** Icon-only button (square aspect ratio) */
  icon: PropTypes.bool,
  
  /** Icon name from icon registry (will be automatically rendered with proper spacing) */
  iconName: PropTypes.string,
  
  /** Icon position relative to text */
  iconPosition: PropTypes.oneOf(['left', 'right']),
  
  /** Full width button */
  fullWidth: PropTypes.bool,

  /**
   * Inline mode — removes padding, min-height, and center alignment so the button
   * sits flush with surrounding content. Use with tertiary or off-site when the
   * button acts as a link within a card title, list item, etc.
   */
  inline: PropTypes.bool,
  
  /** Loading state (disabled + spinner) */
  loading: PropTypes.bool,
  
  /** Disabled state */
  disabled: PropTypes.bool,
  
  /** Button type attribute */
  type: PropTypes.oneOf(['button', 'submit', 'reset']),

  /** Force button to have hover state styles */
  forceHoveredStyle: PropTypes.bool,
  
  /** Click handler */
  onClick: PropTypes.func,

  /** Whether the button should manage it's own loading state */
  autoLoading: PropTypes.bool,
  
  /** Additional CSS classes */
  className: PropTypes.string,
  
  /** Inline styles to override defaults */
  style: PropTypes.object
}

export default WingsButton
